import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const sel = "h-10 rounded-md border border-input bg-white px-3 text-sm";

// Aset dibuat lewat /assets supaya kode per Hub di-claim ulang secara atomik saat disimpan.
export const AssetToolbar = ({ rows, onChanged }) => {
  const [meta, setMeta] = useState(null);
  const [hubId, setHubId] = useState("");
  const [color, setColor] = useState("Black");
  const [bulkColor, setBulkColor] = useState("Black");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadMeta = () => api.get("/asset-meta").then((r) => {
    setMeta(r.data);
    setHubId((h) => h || r.data.hubs[0]?.hub_id || "");
  }).catch(() => setMeta(null));

  useEffect(() => { loadMeta(); }, []);

  const hub = (meta?.hubs || []).find((h) => h.hub_id === hubId);
  const unknown = rows.filter((r) => (r.table_color || "Unknown") === "Unknown");

  const addAsset = async () => {
    setBusy(true);
    try {
      const r = await api.post("/assets", { branch_id: hubId, table_color: color });
      toast.success(`${r.data.unit_code} dibuat · ${r.data.table_color}`);
      loadMeta(); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat aset");
    } finally { setBusy(false); }
  };

  const applyBulk = async () => {
    setBusy(true);
    try {
      const r = await api.put("/assets/bulk-color", { unit_ids: picked, table_color: bulkColor });
      toast.success(`${r.data.updated} aset jadi ${r.data.table_color}`);
      setPicked([]); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah warna");
    } finally { setBusy(false); }
  };

  const toggle = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  return (
    <div
      className="mt-5 rounded-lg border border-black/10 bg-white p-4"
      data-testid="asset-toolbar"
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-ink-soft">Hub</span>
          <select data-testid="asset-hub" className={`${sel} mt-1 block`} value={hubId}
            onChange={(e) => setHubId(e.target.value)}>
            {(meta?.hubs || []).map((h) => (
              <option key={h.hub_id} value={h.hub_id}>{h.name} · {h.units_total} meja</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-ink-soft">Warna meja</span>
          <select data-testid="asset-color" className={`${sel} mt-1 block`} value={color}
            onChange={(e) => setColor(e.target.value)}>
            {(meta?.table_colors || ["Black", "White"]).map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-ink-soft">Asset ID</span>
          <input
            data-testid="asset-id-preview"
            value={hub?.next_code || ""}
            readOnly
            aria-label="Generated Asset ID"
            className={`${sel} mt-1 block w-40 bg-neutral-50 font-semibold text-ink-soft`}
          />
        </label>
        <button
          data-testid="asset-add"
          disabled={busy || !hubId}
          onClick={addAsset}
          className={[
            "lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold",
            "disabled:opacity-50",
          ].join(" ")}
        >
          + Tambah Aset
        </button>
      </div>
      <p className="mt-2 text-xs text-ink-soft">
        Asset ID dibuat otomatis per Hub. Celah nomor hanya dipakai ulang jika aset benar-benar
        dihapus tanpa riwayat operasional; nomor arsip atau bersejarah tetap dipesan.
      </p>

      {unknown.length > 0 && (
        <div className="mt-4 border-t border-black/5 pt-4" data-testid="asset-bulk-color">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{unknown.length} aset belum punya warna</span>
            <button data-testid="asset-pick-all" onClick={() => setPicked(unknown.map((u) => u.id))}
              className="rounded-md border border-black/15 px-3 py-1.5 text-xs hover:border-ink">
              Pilih semua
            </button>
            <select data-testid="asset-bulk-value" className={sel} value={bulkColor}
              onChange={(e) => setBulkColor(e.target.value)}>
              {(meta?.table_colors || ["Black", "White"]).map((c) => <option key={c}>{c}</option>)}
            </select>
            <button
              data-testid="asset-bulk-apply"
              disabled={busy || !picked.length}
              onClick={applyBulk}
              className={[
                "rounded-md bg-ink px-4 py-2 text-xs font-semibold text-cream",
                "disabled:opacity-40",
              ].join(" ")}
            >
              Terapkan ke {picked.length} aset
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {unknown.map((u) => (
              <button
                key={u.id}
                data-testid={`asset-pick-${u.unit_code}`}
                onClick={() => toggle(u.id)}
                className={`rounded-md border px-3 py-1.5 text-xs ${picked.includes(u.id)
                  ? "border-ink bg-ink text-cream"
                  : "border-black/15 hover:border-ink"}`}
              >
                {u.unit_code}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
